import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search,
  Filter,
  FlaskConical,
  MapPin,
  Wifi,
  Clock,
  DollarSign,
  Users,
  ChevronLeft,
  ChevronRight,
  X,
  Award,
  SlidersHorizontal,
} from 'lucide-react';
import { STUDY_CATEGORIES } from '@apex/shared';
import { api } from '@/lib/api';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] } },
};

interface Study {
  id: string;
  title: string;
  description: string;
  compensation_amount: number;
  compensation_type: string;
  duration_minutes: number;
  location: string | null;
  is_remote: boolean;
  min_reliability_score: number;
  max_participants: number;
  current_participants: number;
  category: string | null;
  researcher_name: string;
  researcher_department: string | null;
  starts_at: string;
  ends_at: string;
}

export default function BrowseStudies() {
  const [studies, setStudies] = useState<Study[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const fetchStudies = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getAvailableStudies({
        page,
        per_page: 12,
        search: search || undefined,
        category: category || undefined,
      });
      setStudies(data.data ?? []);
      setTotalPages(data.total_pages ?? 1);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [page, search, category]);

  useEffect(() => {
    fetchStudies();
  }, [fetchStudies]);

  // Debounced search
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleCategoryChange = (cat: string) => {
    setCategory(cat === category ? '' : cat);
    setPage(1);
  };

  const spotsLeft = (study: Study) => study.max_participants - study.current_participants;

  return (
    <motion.div variants={fadeUp} initial="hidden" animate="visible" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="page-header">Browse Studies</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Find paid research opportunities that match your profile
        </p>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search studies by title, description, or researcher..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="input-field pl-11"
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`btn-secondary text-sm flex-shrink-0 ${showFilters ? 'bg-brand-50 dark:bg-brand-900/20 border-brand-200 dark:border-brand-800' : ''}`}
        >
          <SlidersHorizontal className="w-4 h-4" />
          Filters
          {category && <span className="w-2 h-2 rounded-full bg-brand-500" />}
        </button>
      </div>

      {/* Category filters */}
      {showFilters && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="flex flex-wrap gap-2"
        >
          {STUDY_CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => handleCategoryChange(cat)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${
                category === cat
                  ? 'bg-brand-500/10 border-brand-500/40 text-brand-600 dark:text-brand-400'
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {cat}
            </button>
          ))}
        </motion.div>
      )}

      {/* Results */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="card p-5 animate-pulse">
              <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-3/4 mb-3" />
              <div className="h-3 bg-gray-200 dark:bg-gray-800 rounded w-1/2 mb-6" />
              <div className="h-3 bg-gray-200 dark:bg-gray-800 rounded w-full mb-2" />
              <div className="h-3 bg-gray-200 dark:bg-gray-800 rounded w-4/5 mb-4" />
              <div className="flex gap-2">
                <div className="h-6 bg-gray-200 dark:bg-gray-800 rounded-full w-16" />
                <div className="h-6 bg-gray-200 dark:bg-gray-800 rounded-full w-20" />
              </div>
            </div>
          ))}
        </div>
      ) : studies.length === 0 ? (
        <div className="text-center py-16">
          <FlaskConical className="w-14 h-14 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
          <p className="text-lg font-semibold text-gray-600 dark:text-gray-400 mb-1">No studies found</p>
          <p className="text-sm text-gray-500 dark:text-gray-500">
            {search || category ? 'Try adjusting your search or filters' : 'Check back later for new studies'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {studies.map((study, i) => (
            <motion.div
              key={study.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05, duration: 0.3 }}
            >
              <Link
                to={`/studies/${study.id}`}
                className="card p-5 flex flex-col h-full group hover:border-brand-200 dark:hover:border-brand-800 transition-all duration-300"
              >
                {/* Top row: Category + Compensation */}
                <div className="flex items-center justify-between mb-3">
                  {study.category && (
                    <span className="badge-brand text-[10px]">{study.category}</span>
                  )}
                  <div className="flex items-center gap-1 ml-auto">
                    <DollarSign className="w-3.5 h-3.5 text-success-500" />
                    <span className="text-lg font-bold text-success-500">{study.compensation_amount}</span>
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-base font-semibold mb-1 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors line-clamp-2">
                  {study.title}
                </h3>

                {/* Researcher */}
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  {study.researcher_name}
                  {study.researcher_department && ` · ${study.researcher_department}`}
                </p>

                {/* Description preview */}
                <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-4 flex-1">
                  {study.description}
                </p>

                {/* Meta row */}
                <div className="flex items-center gap-3 flex-wrap text-xs text-gray-500 dark:text-gray-400">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {study.duration_minutes} min
                  </span>
                  <span className="flex items-center gap-1">
                    {study.is_remote ? (
                      <>
                        <Wifi className="w-3.5 h-3.5 text-accent-500" />
                        <span className="text-accent-500">Remote</span>
                      </>
                    ) : (
                      <>
                        <MapPin className="w-3.5 h-3.5" />
                        {study.location || 'On campus'}
                      </>
                    )}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" />
                    {spotsLeft(study)} spots left
                  </span>
                  {study.min_reliability_score > 0 && (
                    <span className="flex items-center gap-1">
                      <Award className="w-3.5 h-3.5 text-warning-500" />
                      {study.min_reliability_score}+ score
                    </span>
                  )}
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="btn-ghost disabled:opacity-40"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="btn-ghost disabled:opacity-40"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </motion.div>
  );
}
